import type {
   CommonReportParams,
   CustomField,
   EntityTypes,
   FormattedOption,
   SortModel,
   StreamResponseConversion,
} from "@/react/prop-types";
import type { FindAssignmentsSortBy } from "@laborchart-modules/common/dist/reql-builder/procedures/enums/find-assignments";
import type { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import type { AppliedRoleOption } from "@laborchart-modules/lc-core-api/dist/api/projects/get-applied-role-options";
import type { CellValueChangeParams, IServerFilter, TableApi } from "@procore/data-table";
import type { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { CannedMessageType } from "@laborchart-modules/common/dist/rethink/schemas/enums/canned-messages";
import type { ListServerFilter } from "../data-table/data-table-utils";
import type { GetAssignmentCreationSupportDataResponse } from "@laborchart-modules/lc-core-api/dist/api/legacy/get-assignment-creation-support-data";

export type CoreApiSort = {
   sort_by?: FindAssignmentsSortBy;
   direction?: Order;
   custom_field_id?: string;
};

export type AssignmentListDataTableProps = {
   fetchAssignmentList: (
      serverFilters: IServerFilter[],
      sortModel: SortModel[],
      startingAfter?: string,
   ) => Promise<any>;
   tableApi: TableApi | undefined;
   handleTableReady: (api: TableApi) => void;
   groupId: string;
   streamJobTitles: (groupId: string) => Promise<StreamResponseConversion>;
   streamViewableStatuses: () => Promise<StreamResponseConversion>;
   streamProjects: (groupId: string) => Promise<StreamResponseConversion>;
   streamPeople: (groupId: string) => Promise<StreamResponseConversion>;
   customFields: CustomField[];
   roleOptions: AppliedRoleOption[];
   isLastNameFirst: boolean;
   savedView: any;
   assignmentCreationSupportData: GetAssignmentCreationSupportDataResponse | null;
   updateAssignment: (params: CellValueChangeParams<any>) => Promise<any>;
};

export type WorkDaysObject = {
   [key in "0" | "1" | "2" | "3" | "4" | "5" | "6"]: boolean;
};

// added entity to the filter for custom_field filtering for assignment list
export interface AssignmentListServerFilter extends ListServerFilter {
   entity?: EntityTypes;
}

export const ALLOCATION_TYPE_OPTIONS: FormattedOption[] = [
   {
      id: "hours",
      label: "views.company.workforce_planning.hours",
   },
   {
      id: "percentage",
      label: "views.company.workforce_planning.percentage",
   },
];

export type CreateAssignmentListReportParams = CommonReportParams & {
   roleOptions: AppliedRoleOption[] | undefined;
};
export type AlertFormValues = {
   date: Date | null;
   time: { id: number; label: string } | null;
};

export type AlertPayload = {
   batch_id: string;
   canned_type: CannedMessageType;
   group_id: string | undefined;
   context: AlertContext;
   detached_day?: number;
   time?: number;
};

export enum AssignmentSendAction {
   SendImmediately = "SendImmediately",
   Schedule = "Schedule",
   SaveDraft = "SaveDraft",
}

export type AssignmentTableBaseProps = {
   tableApi?: TableApi;
   selectionInProgress: boolean;
   setManuallySelectedRowData: React.Dispatch<React.SetStateAction<any>>;
   selectedRows: any[];
};

export type ConfirmDeleteModalAssignmentsPropTypes = AssignmentTableBaseProps & {
   successText: string;
   deleteText: string;
   headerText: string;
   highlightedText: string;
   errorText: string;
   id?: string;
   isBulk?: boolean;
};

export type BulkEditAssignmentTearsheetPropTypes = AssignmentTableBaseProps & {
   customFields: CustomField[];
};

export enum FieldMappings {
   START_DATE = "start_date",
   END_DATE = "end_date",
   START_TIME = "start_time",
   END_TIME = "end_time",
   STATUS = "status",
   COST_CODE_NAME = "cost_code_name",
   LABEL_NAME = "label_name",
   PERCENT_ALLOCATED = "percent_allocated",
}
